/* eslint-disable */
/**
 * HjernePanel.jsx — Hjerne-dashboard med interaktiv vidensgraf
 *
 * Faner:
 *   OVERSIGT   — KPI-kort, puls, viden-fordeling, agenter, kommunikation
 *   VIDENSGRAF — D3 force-directed graf formet som menneskehjerne
 *
 * Datapunkter og status-mærkning:
 *   /api/os/hjerne/overblik          → LIVE  (agenter, ulæste, laering_uge, beslutninger)
 *   /api/os/hjerne/viden/seneste     → LIVE  (seneste hjerne_viden-skriv — puls)
 *   /api/os/hjerne/vaekst            → LIVE  (firma_viden_noder fordeling, laering-antal)
 *   /api/os/hjerne/viden             → LIVE  (seneste hjerne_viden-poster)
 *   /api/os/hjerne/laering           → LIVE  (seneste hjerne_laering-poster)
 *   /api/os/hjerne/kommunikation     → LIVE  (agent-til-agent kommunikation)
 *   /api/os/hjerne/alle-noder-graf   → LIVE  (graf-noder med koordinater)
 *   /api/os/hjerne/vidensgraf-kanter → LIVE  (kanter fra os.vidensgraf)
 *
 * Regler:
 *   - Ingen fake/hardkodede tal
 *   - Puls: grøn < 24t, amber > 24t (neutralt vist — ikke dramatiseret)
 *   - TomTilstand hvor data mangler eller endpoint ikke svarer
 *   - Søgning via /api/os/hjerne/viden?soeg=...
 *   - MONTERER IKKE HjerneDashboard.jsx / HjerneVaekst.jsx
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import HjerneGrafPanel from './HjerneGrafPanel';
import {
  KpiKort,
  StatusBadge,
  TomTilstand,
  DataTabel,
  TidslinjeListe,
  GrafLinje,
  SektionsHeader,
  AfventerBadge,
  useApiPolling,
  STATUS,
  TEKST,
  BG,
  KANT,
  RADIUS,
} from '../komponenter/index';
import { Boks } from '../ui/Boks';

// ─── Hjælpefunktioner ─────────────────────────────────────────────────────────

function formatRelativTid(iso) {
  if (!iso) return '—';
  const nu = Date.now();
  const ms = nu - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1)   return 'Lige nu';
  if (min < 60)  return `${min} min siden`;
  const t = Math.floor(min / 60);
  if (t < 24)    return `${t} time${t !== 1 ? 'r' : ''} siden`;
  const d = Math.floor(t / 24);
  return `${d} dag${d !== 1 ? 'e' : ''} siden`;
}

function formatDato(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('da-DK', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function pulsFarve(iso) {
  if (!iso) return STATUS.ikkeForbundet;
  const t = (Date.now() - new Date(iso).getTime()) / 3600000; // timer
  if (t < 24)  return STATUS.live;
  if (t < 168) return STATUS.opmærksomhed;  // < 1 uge
  return STATUS.fejl;
}

function pulsBadge(iso) {
  if (!iso) return 'IKKE FORBUNDET';
  const t = (Date.now() - new Date(iso).getTime()) / 3600000;
  if (t < 24)  return 'LIVE';
  if (t < 168) return 'OPMÆRKSOMHED';
  return 'FEJL';
}

function videnTypeFarve(type) {
  const map = {
    BESLUTNING:    STATUS.konfigureret,
    INDSIGT:       STATUS.ai,
    FEJL:          STATUS.fejl,
    BEST_PRACTICE: STATUS.live,
    'MØNSTER':     STATUS.opmærksomhed,
  };
  return map[type] || STATUS.ikkeForbundet;
}

function nodeTypeMærkat(type) {
  const map = {
    fakta:       'Fakta',
    begreb:      'Begreb',
    entitet:     'Entitet',
    regel:       'Regel',
    procedure:   'Procedure',
    statistik:   'Statistik',
    'klient-meta': 'Klient-meta',
    'copilot-chat': 'Chat',
    lov:         'Lov',
    strategi:    'Strategi',
  };
  return map[type] || type;
}


// ─── Hjerne-puls panel ───────────────────────────────────────────────────────

function HjernePulsPanel({ pulsData, loading }) {
  if (loading) {
    return (
      <Boks>
        <SektionsHeader label="Hjerne-puls" style={{ marginBottom: 12 }} />
        <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter puls…</div>
      </Boks>
    );
  }

  const senest = pulsData?.oprettet_kl || null;
  const farve  = pulsFarve(senest);
  const badge  = pulsBadge(senest);
  const relativ = formatRelativTid(senest);
  const absolut = formatDato(senest);

  return (
    <Boks>
      <SektionsHeader
        label="Hjerne-puls"
        højreIndhold={<StatusBadge status={badge} puls={badge === 'LIVE'} />}
        style={{ marginBottom: 14 }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* Puls-indikator */}
        <div style={{
          width:        56,
          height:       56,
          borderRadius: '50%',
          background:   `${farve}18`,
          border:       `2px solid ${farve}40`,
          display:      'flex',
          alignItems:   'center',
          justifyContent: 'center',
          flexShrink:   0,
        }}>
          <div style={{
            width:        20,
            height:       20,
            borderRadius: '50%',
            background:   farve,
            boxShadow:    badge === 'LIVE' ? `0 0 10px ${farve}` : 'none',
          }} />
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: farve, lineHeight: 1.1 }}>
            {relativ}
          </div>
          <div style={{ fontSize: 12, color: TEKST.sekundær, marginTop: 4 }}>
            Seneste hjerne-skriv: {senest ? absolut : 'Ingen data'}
          </div>
          {badge === 'OPMÆRKSOMHED' && (
            <div style={{ fontSize: 11, color: STATUS.opmærksomhed, marginTop: 6, fontWeight: 600 }}>
              Hjernen har ikke vokset de seneste 24 timer. OS kører ikke autonomt (se CLAUDE.md §5.9).
            </div>
          )}
          {badge === 'FEJL' && (
            <div style={{ fontSize: 11, color: STATUS.fejl, marginTop: 6, fontWeight: 600 }}>
              Hjernen er ikke opdateret i over en uge. Autonomt OS-loop er inaktivt.
            </div>
          )}
          {badge === 'IKKE FORBUNDET' && (
            <div style={{ fontSize: 11, color: STATUS.ikkeForbundet, marginTop: 6 }}>
              Ingen hjerne_viden-poster fundet.
            </div>
          )}
        </div>
      </div>
      <div style={{ marginTop: 12, fontSize: 11, color: TEKST.sekundær }}>
        Kilde: os.hjerne_viden — nyeste post
        <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
      </div>
    </Boks>
  );
}

// ─── Viden-søgning ────────────────────────────────────────────────────────────

function VidenSoegning() {
  const [soegTekst, setSoegTekst] = useState('');
  const [soegResultater, setSoegResultater] = useState(null);
  const [soegLoading, setSoegLoading] = useState(false);
  const [soegFejl, setSoegFejl] = useState(null);
  const timerRef = useRef(null);

  const soeg = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setSoegResultater(null);
      setSoegFejl(null);
      return;
    }
    setSoegLoading(true);
    setSoegFejl(null);
    try {
      const res = await fetch(`/api/os/hjerne/viden?soeg=${encodeURIComponent(q.trim())}&limit=10`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSoegResultater(data.viden || []);
    } catch (err) {
      setSoegFejl('Søgning fejlede: ' + err.message);
      setSoegResultater(null);
    } finally {
      setSoegLoading(false);
    }
  }, []);

  const handleSoegInput = (e) => {
    const v = e.target.value;
    setSoegTekst(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => soeg(v), 400);
  };

  return (
    <Boks>
      <SektionsHeader label="Viden-søgning" style={{ marginBottom: 12 }} />
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <input
          type="text"
          value={soegTekst}
          onChange={handleSoegInput}
          placeholder="Søg i hjerne_viden — fx 'pris', 'CPR', 'deploy'…"
          style={{
            width:           '100%',
            boxSizing:       'border-box',
            background:      'rgba(255,255,255,0.05)',
            border:          `1px solid ${KANT.standard}`,
            borderRadius:    RADIUS.knap,
            padding:         '9px 14px',
            fontSize:        13,
            color:           TEKST.primær,
            outline:         'none',
            fontFamily:      'inherit',
          }}
        />
        {soegLoading && (
          <span style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            fontSize: 11, color: TEKST.sekundær,
          }}>
            Søger…
          </span>
        )}
      </div>

      {soegFejl && (
        <div style={{ fontSize: 12, color: STATUS.fejl, marginBottom: 10 }}>{soegFejl}</div>
      )}

      {soegResultater !== null && soegResultater.length === 0 && (
        <TomTilstand
          titel="Ingen resultater"
          besked={`Ingen hjerne_viden matcher "${soegTekst}"`}
          ikon="◎"
          badge="LIVE"
        />
      )}

      {soegResultater && soegResultater.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {soegResultater.map((v, i) => (
            <div key={v.id || i} style={{
              background:   'rgba(255,255,255,0.03)',
              border:       `1px solid ${KANT.fin}`,
              borderRadius: RADIUS.knap,
              padding:      '10px 14px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <StatusBadge
                  status={v.bekraeftet_af_rene ? 'LIVE' : 'KONFIGURERET'}
                  tekst={v.viden_type}
                  lille
                />
                <span style={{ fontSize: 11, color: TEKST.sekundær }}>{v.afdeling}</span>
                {v.bekraeftet_af_rene && (
                  <span style={{ fontSize: 10, color: STATUS.live, marginLeft: 'auto' }}>
                    René-bekræftet
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: TEKST.primær, marginBottom: 4, fontWeight: 500 }}>
                {v.kontekst?.slice(0, 100)}{v.kontekst?.length > 100 ? '…' : ''}
              </div>
              <div style={{ fontSize: 11, color: TEKST.sekundær }}>
                {v.laering?.slice(0, 160)}{v.laering?.length > 160 ? '…' : ''}
              </div>
              {v.anvendt_antal > 0 && (
                <div style={{ fontSize: 10, color: TEKST.dæmpet, marginTop: 4 }}>
                  Brugt {v.anvendt_antal} gang{v.anvendt_antal !== 1 ? 'e' : ''}
                </div>
              )}
            </div>
          ))}
          <div style={{ fontSize: 11, color: TEKST.sekundær, marginTop: 4 }}>
            Kilde: os.hjerne_viden · fuldsøgning via plainto_tsquery
            <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
          </div>
        </div>
      )}

      {soegTekst.trim().length < 2 && soegResultater === null && (
        <div style={{ fontSize: 12, color: TEKST.sekundær }}>
          Skriv mindst 2 tegn for at søge i hjerne_viden (dansk fuldtekstsøgning).
        </div>
      )}
    </Boks>
  );
}

// ─── PANEL ────────────────────────────────────────────────────────────────────

export default function HjernePanel() {
  // ── Data-fetching ──────────────────────────────────────────────────────────

  const {
    data:      overblik,
    loading:   overblikLoading,
    fejl:      overblikFejl,
    opdateret: overblikOpdateret,
    refresh:   overblikRefresh,
  } = useApiPolling('/os/hjerne/overblik', 90000);

  const {
    data:      pulsSvar,
    loading:   pulsLoading,
    refresh:   pulsRefresh,
  } = useApiPolling('/os/hjerne/viden/seneste', 60000);

  const {
    data:      vaekst,
    loading:   vaekstLoading,
    refresh:   vaekstRefresh,
  } = useApiPolling('/os/hjerne/vaekst', 120000);

  const {
    data:      videnSvar,
    loading:   videnLoading,
  } = useApiPolling('/os/hjerne/viden?limit=10&status=aktiv', 60000);

  const {
    data:      laeringSvar,
    loading:   laeringLoading,
  } = useApiPolling('/os/hjerne/laering?limit=10', 60000);

  const {
    data:      kommSvar,
    loading:   kommLoading,
  } = useApiPolling('/os/hjerne/kommunikation?limit=15', 60000);

  // ── Refresh alle ───────────────────────────────────────────────────────────
  const [refreshing, setRefreshing] = useState(false);
  const refreshAlt = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([overblikRefresh(), pulsRefresh(), vaekstRefresh()]);
    setRefreshing(false);
  }, [overblikRefresh, pulsRefresh, vaekstRefresh]);

  // ── Udled data ─────────────────────────────────────────────────────────────

  const senestPuls = pulsSvar?.oprettet_kl || null;
  const stats      = vaekst?.stats || null;

  // KPI-tal
  const antalFirmaVidenNoder = stats?.firmaviden    ?? null;
  const antalHjerneViden     = overblik?.laering_uge ?? null;   // ugentlige laering
  const antalAgenter         = overblik?.agenter?.length ?? null;
  const antalAktiveAgenter   = overblik?.agenter?.filter(a =>
    a.aktiv_beregnet !== false && a.status !== 'inaktiv'
  ).length ?? null;
  const afventerBeslutninger = overblik?.afventende_beslutninger ?? null;

  // Viden-noder fordeling — sparkline per type
  const nodeTypeData = stats ? [
    { label: 'Fakta',     vaerdi: stats.noder_fakta     || 0 },
    { label: 'Begreb',    vaerdi: stats.noder_begreb     || 0 },
    { label: 'Entitet',   vaerdi: stats.noder_entitet    || 0 },
    { label: 'Regel',     vaerdi: stats.noder_regel      || 0 },
    { label: 'Procedure', vaerdi: stats.noder_procedure  || 0 },
    { label: 'Statistik', vaerdi: stats.noder_statistik  || 0 },
  ].filter(d => d.vaerdi > 0) : null;

  // Seneste hjerne_viden-poster → TidslinjeListe
  const videnTidslinje = (() => {
    const rækker = videnSvar?.viden;
    if (!Array.isArray(rækker)) return [];
    return rækker.map((v, i) => ({
      id:    v.id || i,
      dato:  v.oprettet_kl,
      titel: v.kontekst?.slice(0, 80) || '—',
      tekst: v.laering?.slice(0, 120) || '',
      farve: videnTypeFarve(v.viden_type),
      ikon:  '●',
      mærkat: v.viden_type,
    }));
  })();

  // Seneste hjerne_laering-poster → TidslinjeListe
  const laeringTidslinje = (() => {
    const rækker = laeringSvar?.laering;
    if (!Array.isArray(rækker)) return [];
    return rækker.map((l, i) => ({
      id:    l.id || i,
      dato:  l.oprettet_kl,
      titel: l.titel || '—',
      tekst: l.indhold?.slice(0, 120) || '',
      farve: STATUS.ai,
      ikon:  '◎',
      mærkat: l.kategori || 'generel',
    }));
  })();

  // Seneste agent-kommunikation → DataTabel
  const kommunikation = kommSvar?.kommunikation || [];
  const kommKolonner = [
    {
      key:    'fra_agent',
      label:  'Fra',
      render: v => <span style={{ fontSize: 12, color: STATUS.ai, fontWeight: 600 }}>{v}</span>,
    },
    {
      key:    'til_agent',
      label:  'Til',
      render: v => <span style={{ fontSize: 12, color: TEKST.sekundær }}>{v}</span>,
    },
    {
      key:    'indhold',
      label:  'Besked',
      render: (v, row) => {
        const tekst = row.indhold || row.besked || '';
        return (
          <span style={{ fontSize: 11, color: TEKST.primær }}>
            {tekst.slice(0, 80)}{tekst.length > 80 ? '…' : ''}
          </span>
        );
      },
    },
    {
      key:    'oprettet_kl',
      label:  'Tid',
      align:  'right',
      render: v => <span style={{ fontSize: 11, color: TEKST.dæmpet }}>{formatRelativTid(v)}</span>,
    },
  ];

  // Agenter-status-tabel
  const agentKolonner = [
    {
      key:    'display_navn',
      label:  'Agent',
      render: (v, row) => (
        <span style={{ fontSize: 12, color: TEKST.primær, fontWeight: 500 }}>
          {v}
        </span>
      ),
    },
    {
      key:    'status',
      label:  'Status',
      render: (v, row) => {
        const aktiv = row.aktiv_beregnet !== false && v !== 'inaktiv';
        return (
          <StatusBadge
            status={aktiv ? 'LIVE' : 'IKKE FORBUNDET'}
            tekst={aktiv ? 'Aktiv' : 'Inaktiv'}
            lille
          />
        );
      },
    },
    {
      key:    'sidst_aktiv',
      label:  'Sidst aktiv',
      align:  'right',
      render: v => (
        <span style={{ fontSize: 11, color: TEKST.sekundær }}>
          {v ? formatRelativTid(v) : '—'}
        </span>
      ),
    },
    {
      key:    'aktuel_opgave',
      label:  'Aktuel opgave',
      render: v => v
        ? <span style={{ fontSize: 11, color: TEKST.sekundær }}>{v.slice(0, 60)}{v.length > 60 ? '…' : ''}</span>
        : <span style={{ fontSize: 11, color: TEKST.dæmpet }}>—</span>,
    },
  ];

  const [aktivFane, setAktivFane] = useState('oversigt');

  // ── Fane-stil ─────────────────────────────────────────────────────────────
  const faneStyle = (id) => ({
    padding:       '7px 18px',
    fontSize:      12,
    fontWeight:    aktivFane === id ? 700 : 500,
    color:         aktivFane === id ? STATUS.ai : TEKST.sekundær,
    background:    aktivFane === id ? `${STATUS.ai}14` : 'transparent',
    border:        aktivFane === id ? `1px solid ${STATUS.ai}30` : `1px solid ${KANT.fin}`,
    borderRadius:  RADIUS.knap,
    cursor:        'pointer',
    transition:    'all 0.15s ease',
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* Panel-header */}
      <SektionsHeader
        label="Hjernen"
        opdateret={overblikOpdateret}
        onRefresh={refreshAlt}
        refreshing={refreshing}
        højreIndhold={
          afventerBeslutninger > 0
            ? <AfventerBadge antal={afventerBeslutninger} label="beslutninger afventer" />
            : null
        }
        style={{ marginBottom: 14 }}
      />

      {/* Fane-switcher */}
      <div style={{
        display:      'flex',
        gap:          8,
        marginBottom: 20,
      }}>
        <button style={faneStyle('oversigt')} onClick={() => setAktivFane('oversigt')}>
          Oversigt
        </button>
        <button style={faneStyle('vidensgraf')} onClick={() => setAktivFane('vidensgraf')}>
          Vidensgraf
        </button>
      </div>

      {/* Vidensgraf-fane */}
      {aktivFane === 'vidensgraf' && <HjerneGrafPanel />}

      {/* Oversigt-fane — al eksisterende kode */}
      {aktivFane === 'oversigt' && (<>

      {/* ── KPI-række ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 22 }}>
        <KpiKort
          label="Viden-noder (firma)"
          value={antalFirmaVidenNoder}
          farve={STATUS.konfigureret}
          ikon="◎"
          badge="LIVE"
          loading={vaekstLoading}
        />
        <KpiKort
          label="Registrerede agenter"
          value={antalAgenter}
          farve={STATUS.ai}
          ikon="◉"
          badge="LIVE"
          loading={overblikLoading}
        />
        <KpiKort
          label="Aktive agenter"
          value={antalAktiveAgenter}
          farve={antalAktiveAgenter > 0 ? STATUS.live : STATUS.ikkeForbundet}
          ikon="▸"
          badge="LIVE"
          loading={overblikLoading}
        />
        <KpiKort
          label="Laering denne uge"
          value={antalHjerneViden}
          farve={STATUS.live}
          ikon="↻"
          badge="LIVE"
          loading={overblikLoading}
        />
        <KpiKort
          label="Laering i alt"
          value={stats?.total_laering}
          farve={STATUS.ai}
          ikon="★"
          badge="LIVE"
          loading={vaekstLoading}
        />
      </div>

      {/* ── To-kolonne: Hjerne-puls + Viden-noder fordeling ─────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 18,
        marginBottom: 22,
      }}>

        {/* Hjerne-puls */}
        <HjernePulsPanel pulsData={pulsSvar} loading={pulsLoading} />

        {/* Viden-noder fordeling per type */}
        <Boks>
          <SektionsHeader label="Viden-noder per type" style={{ marginBottom: 12 }} />
          {vaekstLoading ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : !nodeTypeData ? (
            <TomTilstand
              titel="Ingen viden-noder"
              besked="Returneres fra /api/os/hjerne/vaekst"
              ikon="◎"
              badge="LIVE"
            />
          ) : (
            <>
              <GrafLinje
                data={nodeTypeData}
                farve={STATUS.konfigureret}
                højde={90}
                fyldt={true}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 10 }}>
                {nodeTypeData.map(d => (
                  <div key={d.label} style={{ fontSize: 11, color: TEKST.sekundær }}>
                    <span style={{ color: TEKST.primær, fontWeight: 600 }}>{d.vaerdi.toLocaleString('da-DK')}</span>
                    {' '}{d.label}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: TEKST.sekundær }}>
                os.firma_viden_noder — {antalFirmaVidenNoder?.toLocaleString('da-DK') || '?'} noder i alt
                <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
              </div>
            </>
          )}
        </Boks>
      </div>

      {/* ── To-kolonne: Seneste viden + Seneste laering ──────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 18,
        marginBottom: 22,
      }}>

        {/* Seneste hjerne_viden-poster */}
        <Boks>
          <SektionsHeader label="Seneste viden (hjerne_viden)" style={{ marginBottom: 12 }} />
          {videnLoading ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : videnTidslinje.length === 0 ? (
            <TomTilstand
              titel="Ingen hjerne_viden-poster"
              besked="Agenter skriver viden via os.hjerne_viden ved opgaveafslutning"
              ikon="◎"
              badge="LIVE"
            />
          ) : (
            <>
              <TidslinjeListe
                items={videnTidslinje}
                tomTekst="Ingen viden-poster"
                loading={videnLoading}
                maxHøjde={280}
              />
              <div style={{ marginTop: 8, fontSize: 11, color: TEKST.sekundær }}>
                os.hjerne_viden · aktive poster · nyeste først
                <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
              </div>
            </>
          )}
        </Boks>

        {/* Seneste hjerne_laering-poster */}
        <Boks>
          <SektionsHeader label="Seneste laering (hjerne_laering)" style={{ marginBottom: 12 }} />
          {laeringLoading ? (
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
          ) : laeringTidslinje.length === 0 ? (
            <TomTilstand
              titel="Ingen hjerne_laering-poster"
              besked="Agenter skriver laeringer ved deploy og opgaveresultater"
              ikon="◎"
              badge="LIVE"
            />
          ) : (
            <>
              <TidslinjeListe
                items={laeringTidslinje}
                tomTekst="Ingen laering"
                loading={laeringLoading}
                maxHøjde={280}
              />
              <div style={{ marginTop: 8, fontSize: 11, color: TEKST.sekundær }}>
                os.hjerne_laering · alle kategorier · nyeste først
                <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
              </div>
            </>
          )}
        </Boks>
      </div>

      {/* ── Agent-aktivitet ──────────────────────────────────────────────────── */}
      <Boks style={{ marginBottom: 22 }}>
        <SektionsHeader label="Agent-status" style={{ marginBottom: 12 }} />
        {overblikLoading ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter agenter…</div>
        ) : !overblik?.agenter || overblik.agenter.length === 0 ? (
          <TomTilstand
            titel="Ingen agenter registreret"
            besked="os.hjerne_agent_status er tom — kør hjerne-sync for at synkronisere"
            ikon="◉"
            badge="LIVE"
          />
        ) : (
          <>
            <DataTabel
              kolonner={agentKolonner}
              rækker={overblik.agenter}
              pageSize={11}
              tomTekst="Ingen agenter"
            />
            <div style={{ marginTop: 8, fontSize: 11, color: TEKST.sekundær }}>
              os.hjerne_agent_status · reel aktivitet fra alle log-kilder
              <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
            </div>
          </>
        )}
      </Boks>

      {/* ── Agent-kommunikation ──────────────────────────────────────────────── */}
      <Boks style={{ marginBottom: 22 }}>
        <SektionsHeader label="Agent-kommunikation" style={{ marginBottom: 12 }} />
        {kommLoading ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter kommunikation…</div>
        ) : kommunikation.length === 0 ? (
          <TomTilstand
            titel="Ingen agent-kommunikation"
            besked="os.hjerne_kommunikation og os.agent_kommunikation er tomme"
            ikon="◑"
            badge="LIVE"
          />
        ) : (
          <>
            <DataTabel
              kolonner={kommKolonner}
              rækker={kommunikation}
              pageSize={10}
              tomTekst="Ingen beskeder"
            />
            <div style={{ marginTop: 8, fontSize: 11, color: TEKST.sekundær }}>
              UNION: os.hjerne_kommunikation + os.agent_kommunikation · seneste 15
              <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
            </div>
          </>
        )}
      </Boks>

      {/* ── Viden-søgning ────────────────────────────────────────────────────── */}
      <VidenSoegning />

      </>)} {/* slut: aktivFane === 'oversigt' */}

    </div>
  );
}
